"use client";

import { cn } from "@/lib/utils";
import type { CricketTemplateData } from "../cricket-template";
import {
  ContactShadow,
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  FloatingProp,
  Floodlight,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
  StadiumStands,
} from "./field-sport-scene-kit";

export type CricketDisplayPreviewProps = {
  data: CricketTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

// Day-night match, seen from behind the bowler's arm: the 22 yards run away
// from the camera down the middle, so the stumps stay clear of both sides'
// lineups and of the score.
const GROUND: PlaneSpec = {
  top: 66,
  depth: 180,
  tilt: 60,
  perspective: 300,
  farWidth: 116,
};
const STUMPS_V = 26; // batting-end stumps, px into the plane
const FAR_STUMPS = projectOnPlane(GROUND, 0.5, STUMPS_V);
const BALL_IN_FLIGHT = projectOnPlane(GROUND, 0.5, 92);

/** Red leather, raised white seam. */
const CricketBall = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_28%,#ff8a7a_0%,#c41c1c_42%,#5a0606_100%)]",
      className,
    )}
    style={{
      boxShadow:
        "inset -1px -2px 3px rgba(40,0,0,0.6), inset 1px 1px 2px rgba(255,190,180,0.5)",
    }}
  >
    <div className="absolute top-[-10%] bottom-[-10%] left-[44%] w-[12%] rotate-[18deg] bg-[#f6ead2] [background-image:repeating-linear-gradient(180deg,transparent_0px,transparent_1.5px,rgba(120,40,30,0.7)_1.5px,rgba(120,40,30,0.7)_2.2px)]" />
    <div className="absolute top-[12%] left-[20%] h-[30%] w-[32%] rounded-full bg-white/35 blur-[0.5px]" />
  </div>
);

/** Three stumps and two bails, standing up off the plane. */
function Stumps({ scale }: { scale: number }) {
  const h = Math.round(24 * scale);
  return (
    <div
      className="relative flex justify-between"
      style={{ height: h, width: Math.round(13 * scale) }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-full w-[3px] rounded-t-[1px] bg-[linear-gradient(90deg,#c9a46a_0%,#fff1d4_45%,#b88c52_100%)] shadow-[0_0_6px_rgba(255,236,200,0.35)]"
        />
      ))}
      <div className="absolute -top-[2px] left-0 h-[2.5px] w-[54%] rounded-full bg-[#fff1d4] shadow-[0_1px_1px_rgba(0,0,0,0.4)]" />
      <div className="absolute -top-[2px] right-0 h-[2.5px] w-[54%] rounded-full bg-[#fff1d4] shadow-[0_1px_1px_rgba(0,0,0,0.4)]" />
    </div>
  );
}

/** Outfield, 30-yard circle and the strip, flat in plane coordinates. */
function GroundMarkings() {
  return (
    <>
      {/* Outfield: striped mowing that converges into the distance */}
      <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,#3d9a47_0%,#3d9a47_6.25%,#338a3d_6.25%,#338a3d_12.5%)]" />
      <div className="absolute inset-0 [background-image:radial-gradient(rgba(0,0,0,0.16)_0.6px,transparent_0.9px),radial-gradient(rgba(210,255,180,0.12)_0.6px,transparent_0.9px)] [background-size:3px_3px,3px_3px] [background-position:0_0,1.5px_1.5px] opacity-60" />

      {/* Boundary rope + the 30-yard fielding circle */}
      <div className="absolute inset-x-0 top-[3px] h-[3px] bg-[linear-gradient(90deg,#f5f5f5,#ffd23f,#f5f5f5,#ffd23f,#f5f5f5)] opacity-80" />
      <div className="absolute top-[86px] left-1/2 h-[156px] w-[156px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-dashed border-white/55" />

      {/* The strip: dry, rolled, with worn bowlers' footmarks */}
      <div className="absolute top-[12px] bottom-[-20px] left-1/2 w-[26px] -translate-x-1/2 bg-[linear-gradient(180deg,#e2c690_0%,#d4b27a_40%,#c9a46c_100%)] shadow-[0_0_0_1px_rgba(120,90,40,0.35)]">
        <div className="absolute inset-0 [background-image:radial-gradient(rgba(110,74,30,0.35)_0.6px,transparent_1px)] [background-size:3px_3px] opacity-70" />
        <div className="absolute inset-x-0 top-[6px] h-[26px] bg-[radial-gradient(ellipse_at_center,rgba(120,80,30,0.35),transparent_70%)]" />
        <div className="absolute inset-x-0 top-[132px] h-[26px] bg-[radial-gradient(ellipse_at_center,rgba(120,80,30,0.35),transparent_70%)]" />
        {/* Batting end: bowling crease through the stumps, popping crease in front, return creases */}
        <div className="absolute inset-x-[-6px] top-[14px] h-[1.5px] bg-white/90" />
        <div className="absolute inset-x-[-10px] top-[22px] h-[1.5px] bg-white/90" />
        <div className="absolute top-[8px] left-[-6px] h-[14px] w-[1.5px] bg-white/80" />
        <div className="absolute top-[8px] right-[-6px] h-[14px] w-[1.5px] bg-white/80" />
        {/* Bowler's end */}
        <div className="absolute inset-x-[-10px] top-[140px] h-[1.5px] bg-white/90" />
        <div className="absolute inset-x-[-6px] top-[148px] h-[1.5px] bg-white/90" />
      </div>
    </>
  );
}

function CricketGround() {
  return (
    <>
      {/* Twilight over the stands: indigo overhead, a last orange band at
          the roofline (inline — px stops tied to the stands' position). */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg,#0b1030 0px,#2a1d5c 12px,#7a3468 24px,#e8743f 34px,#ffb066 38px,#1d1420 44px)",
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          top: 14,
          left: "62%",
          width: 110,
          height: 40,
          background:
            "radial-gradient(closest-side, rgba(255,180,110,0.45), transparent)",
        }}
      />

      <div className="absolute inset-x-0" style={{ top: 38, height: 32 }}>
        <StadiumStands
          className="h-full"
          crowdA="120,180,255"
          crowdB="255,170,60"
          crowdOpacity={0.65}
          baseClassName="bg-[linear-gradient(180deg,#120a1a_0%,#1d1420_100%)]"
          boardClassName="bg-[linear-gradient(90deg,#1b2a6b_0%,#2f6bff_18%,#e8f0ff_34%,#ffb23a_52%,#1b2a6b_70%,#2f6bff_86%,#e8f0ff_100%)] opacity-80 shadow-[0_0_12px_rgba(80,140,255,0.5)]"
        />
      </div>

      <PerspectivePlane plane={GROUND}>
        <GroundMarkings />
      </PerspectivePlane>

      {/* Floodlit haze at the boundary and pools on the square */}
      <div className="absolute inset-x-0 top-[58px] h-[30px] bg-[linear-gradient(180deg,transparent,rgba(255,240,214,0.14)_50%,transparent)]" />
      <Floodlight
        side="left"
        x="3%"
        y={6}
        beamTo="34%"
        pylon
        beamOpacity={0.14}
      />
      <Floodlight
        side="right"
        x="3%"
        y={6}
        beamTo="34%"
        pylon
        beamOpacity={0.14}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_34%_34%_at_50%_58%,rgba(255,248,226,0.16),transparent)]" />

      {/* Batting-end stumps, standing on their crease */}
      <div
        className="absolute -translate-x-1/2 -translate-y-full"
        style={{ left: FAR_STUMPS.left, top: FAR_STUMPS.top }}
      >
        <ContactShadow className="-bottom-[3px] left-[30%] h-[5px] w-[22px]" />
        <Stumps scale={FAR_STUMPS.scale} />
      </div>

      {/* Depth */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_62%,rgba(0,14,6,0.42)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_95%_at_50%_45%,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
    </>
  );
}

export function CricketDisplayPreview({
  data,
  participants,
}: CricketDisplayPreviewProps) {
  const toSide = (team: "your-team" | "opponent-team") =>
    participants
      .filter((participant) => data?.positions?.[participant.id] === team)
      .map((participant) => ({
        ...participant,
        handle: participant.username ?? undefined,
      }));
  const yourTeam = toSide("your-team");
  const oppTeam = toSide("opponent-team");

  const showScore = !!data && data.hasScore !== false;
  const yourWon =
    data?.result === "won-by-runs" || data?.result === "won-by-wickets";
  const opponentWon = data?.result === "loss";
  const isDraw = data?.result === "draw" || data?.result === "tie";

  const sideLabel = (team: typeof yourTeam, emptyLabel: string) => {
    const firstName = team[0]?.displayName.split(" ")[0] || emptyLabel;
    return team.length <= 1 ? firstName : `${firstName} +${team.length - 1}`;
  };
  const yourLabel = sideLabel(yourTeam, "You");
  const oppositionLabel = sideLabel(oppTeam, "Opposition");

  const yourWinMethod =
    data?.result === "won-by-wickets"
      ? "Wkts"
      : data?.result === "won-by-runs"
        ? "Runs"
        : null;
  const opponentWinMethod = (() => {
    if (data?.result !== "loss") return null;
    if (data.chasingTeam === "your-team") return "Runs";
    if (data.chasingTeam === "opponent-team") return "Wkts";
    return null;
  })();

  const matchState: FieldMatchState =
    yourTeam.length === 0 && oppTeam.length === 0
      ? "EMPTY"
      : showScore
        ? "FULL"
        : "NO_SCORE";

  const resultText = yourWon
    ? `${yourLabel} won${yourWinMethod ? ` · ${yourWinMethod}` : ""}`
    : opponentWon
      ? `${oppositionLabel} won${opponentWinMethod ? ` · ${opponentWinMethod}` : ""}`
      : isDraw
        ? data?.result === "tie"
          ? "Tie"
          : "Draw"
        : "In progress";

  const yourOvers = data?.yourInnings.overs;
  const oppOvers = data?.opponentInnings.overs;

  return (
    <FieldMatchFrame
      label="Cricket"
      frameClassName="bg-[#0a0f1a]"
      matchState={matchState}
      scene={<CricketGround />}
      yourTeam={yourTeam}
      opponentTeam={oppTeam}
      yourWinner={showScore && yourWon}
      opponentWinner={showScore && opponentWon}
      linkToProfiles
      bandClassName="top-[52px] bottom-1"
      decor={
        matchState !== "NO_SCORE" ? null : (
          // A delivery on its way down the strip.
          <div
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: BALL_IN_FLIGHT.left, top: BALL_IN_FLIGHT.top - 10 }}
          >
            <ContactShadow className="-bottom-[14px] left-1/2 h-[5px] w-[14px] -translate-x-1/2" />
            <FloatingProp amplitude={3} duration={3.2}>
              <CricketBall className="h-[13px] w-[13px] @sm:h-[15px] @sm:w-[15px]" />
            </FloatingProp>
          </div>
        )
      }
      center={
        matchState === "FULL" && data ? (
          <>
            <FieldScore
              transitionKey={`${data.yourInnings.runs}-${data.opponentInnings.runs}`}
              left={
                <>
                  {data.yourInnings.runs}
                  <span className="text-[0.62em] text-white/60">
                    /{data.yourInnings.wickets}
                  </span>
                </>
              }
              right={
                <>
                  {data.opponentInnings.runs}
                  <span className="text-[0.62em] text-white/60">
                    /{data.opponentInnings.wickets}
                  </span>
                </>
              }
              leftWinner={yourWon}
              rightWinner={opponentWon}
              valueClassName="text-[30px] @xs:text-[40px] @sm:text-[50px] @md:text-[58px]"
              delimiter={
                <span className="h-[4px] w-3 shrink-0 rounded-full bg-[#fde3b8] shadow-[0_0_8px_rgba(253,227,184,0.6)]" />
              }
            />
            <div className="flex flex-col items-center gap-1 rounded-2xl bg-black/50 px-3 py-1.5 ring-1 ring-white/10">
              {typeof yourOvers === "number" || typeof oppOvers === "number" ? (
                <span className="flex gap-3 text-[10px] font-bold whitespace-nowrap text-white/55 tabular-nums">
                  {typeof yourOvers === "number" ? (
                    <span>
                      <span className="text-white/90">{yourOvers}</span> ov
                    </span>
                  ) : null}
                  {typeof oppOvers === "number" ? (
                    <span>
                      <span className="text-white/90">{oppOvers}</span> ov
                    </span>
                  ) : null}
                </span>
              ) : null}
              <span
                className={cn(
                  "max-w-[170px] truncate rounded-full px-2.5 py-0.5 text-[9px] font-black tracking-[0.18em] uppercase @sm:max-w-none @sm:text-[10px]",
                  yourWon || opponentWon
                    ? "bg-[#fde3b8]/15 text-[#fde3b8]"
                    : isDraw
                      ? "bg-white/10 text-white/75"
                      : "bg-[#b81d1d]/25 text-[#ffb03a]",
                )}
              >
                {resultText}
              </span>
            </div>
          </>
        ) : null
      }
    />
  );
}
